// src/components/user/controltower/TowerSidebar.jsx
import React from 'react';
import { AlertTriangle, MapPinned, Package, Search, Truck, X } from 'lucide-react';
import { RegularDeliveryCard } from './RegularDeliveryCard';

const FILTERS = [
  { value: 'all', label: 'Todas', icon: Package },
  { value: 'alerts', label: 'Alertas', icon: AlertTriangle },
  { value: 'encamino', label: 'En camino', icon: Truck },
  { value: 'pending', label: 'Pendientes', icon: MapPinned },
];

export function TowerSidebar({
  showList,
  searchQuery,
  onSearchChange,
  filteredRegular,
  totalRegular,
  selectedDelivery,
  onDeliveryClick,
  alertsByDeliveryId,
  alertSummary,
  intelligenceByDeliveryId,
  onIntelligenceClick,
  listFilter,
  setListFilter,
}) {
  return (
    <div className={`
      ${showList ? 'flex' : 'hidden lg:flex'}
      flex-col bg-white border-slate-200 z-20 shadow-2xl lg:shadow-none
      absolute lg:relative inset-x-0 bottom-0 lg:inset-auto
      h-[72vh] max-h-[calc(100%-3.5rem)] lg:h-auto lg:max-h-none lg:w-[25rem]
      rounded-t-3xl lg:rounded-none border-t lg:border-t-0 lg:border-r
    `}>
      <div className="lg:hidden flex justify-center pt-2 pb-1">
        <div className="w-12 h-1.5 rounded-full bg-slate-300" />
      </div>

      <div className="px-4 py-3 border-b border-slate-200">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-extrabold text-slate-900 font-montserrat">Entregas Lima</h2>
            <p className="text-xs text-slate-500 font-montserrat">
              {totalRegular} entrega{totalRegular !== 1 ? 's' : ''} - {alertSummary?.affectedDeliveries || 0} con alerta
            </p>
          </div>
          {alertSummary?.total > 0 && (
            <div className={`rounded-xl px-2.5 py-1 text-xs font-extrabold font-montserrat flex items-center gap-1 ${alertSummary.high > 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
              <AlertTriangle className="w-3.5 h-3.5" />
              {alertSummary.total}
            </div>
          )}
        </div>
      </div>

      <div className="p-3 border-b border-slate-200 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, telefono, ID o alerta..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-8 py-2.5 border border-slate-200 rounded-xl text-sm font-montserrat focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
          />
          {searchQuery && (
            <button onClick={() => onSearchChange('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-lg hover:bg-slate-100" aria-label="Limpiar busqueda">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>

        <div className="flex gap-1.5 overflow-x-auto pb-0.5">
          {FILTERS.map(filter => {
            const Icon = filter.icon;
            const active = listFilter === filter.value;
            return (
              <button
                key={filter.value}
                onClick={() => setListFilter(filter.value)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-bold font-montserrat border transition-colors ${
                  active
                    ? filter.value === 'alerts'
                      ? 'bg-amber-100 text-amber-700 border-amber-200'
                      : 'bg-brand-primary text-white border-brand-primary'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {filter.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-500 font-montserrat">
          {filteredRegular.length} resultado{filteredRegular.length !== 1 ? 's' : ''}
        </p>
        {listFilter === 'alerts' && alertSummary?.high > 0 && (
          <p className="text-[11px] font-bold text-red-600 font-montserrat">
            {alertSummary.high} critica{alertSummary.high !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto divide-y divide-slate-100 overscroll-contain">
        {filteredRegular.length === 0 ? (
          <EmptyState
            icon={listFilter === 'alerts' ? <AlertTriangle className="w-12 h-12 text-slate-300" /> : <Package className="w-12 h-12 text-slate-300" />}
            message={searchQuery ? 'Sin resultados para tu busqueda' : listFilter === 'alerts' ? 'No hay entregas con alertas activas' : 'No hay entregas disponibles'}
          />
        ) : (
          filteredRegular.map(d => (
            <RegularDeliveryCard
              key={d.id}
              delivery={d}
              alerts={alertsByDeliveryId[d.id] || []}
              intelligence={intelligenceByDeliveryId?.[d.id]}
              isSelected={selectedDelivery?.type === 'regular' && selectedDelivery?.data?.id === d.id}
              onClick={() => onDeliveryClick({ type: 'regular', data: d })}
              onIntelligenceClick={onIntelligenceClick}
            />
          ))
        )}
      </div>
    </div>
  );
}

function EmptyState({ icon, message }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
      {icon}
      <p className="mt-3 text-sm text-slate-500 font-montserrat">{message}</p>
    </div>
  );
}
